/**
 * Slice 20a — Unit setup: baseline items + thin Appliances / Guide / PM shortcuts.
 */
import { useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  useUnitItems,
  useCreateUnitItem,
  useUpdateUnitItem,
  useDeleteUnitItem,
  type PropertyUnitItem,
} from "@/hooks/useUnitItems";
import { usePropertyRooms } from "@/hooks/usePropertyRooms";
import { useAppliances, useCreateAppliance, useDeleteAppliance, useUpdateAppliance, type Appliance } from "@/hooks/useAppliances";
import {
  usePropertyGuideEntries,
  useCreatePropertyGuideEntry,
} from "@/hooks/usePropertyGuideEntries";
import { format } from "date-fns";
import {
  usePropertyPreventativeMaintenance,
  useCreateCustomPreventativeMaintenance,
  useDisablePreventativeMaintenance,
  getFrequencyLabel,
  type PreventativeFrequency,
} from "@/hooks/usePreventativeMaintenance";
import { usePropertyAssignableMembers } from "@/hooks/usePropertyAssignableMembers";
import { supabase } from "@/integrations/supabase/client";
import { LocationStampsSettings, useLocationStampsSummary } from "@/components/owner/LocationStampsSettings";
import { ArrivalChecklistSettings, useArrivalChecklistSummary } from "@/components/owner/ArrivalChecklistSettings";
import { prepareImageFileForUpload } from "@/lib/prepareImageForUpload";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";
import { MediaThumb } from "@/components/media/MediaThumb";
import { sortUnitItems } from "@/lib/unitItems";
import { UnitQuickCheckSection } from "@/components/owner/UnitQuickCheckSection";
import PhotoGroupsSection from "@/components/owner/PhotoGroupsSection";
import { UnitSubTabs, type UnitSub } from "@/components/owner/UnitSubTabs";
import { UnitAppliancesPage } from "@/components/owner/UnitAppliancesPage";
import { UnitGuideClipsPage } from "@/components/owner/UnitGuideClipsPage";
import { UnitPartsPage } from "@/components/owner/UnitPartsPage";
import { PartDialog } from "@/components/owner/PartDialog";
import { usePropertyParts } from "@/hooks/usePropertyParts";
import type { PropertyPart } from "@/lib/parts";
import { itemLabel } from "@/lib/photoGroups";

const PM_FREQUENCIES: PreventativeFrequency[] = [
  "monthly",
  "quarterly",
  "bi_annual",
  "annual",
];

type SidePanel = "appliances" | "guide" | "pm" | "stamps" | "arrival" | null;

interface UnitSetupTabProps {
  propertyId: string;
  className?: string;
}

export default function UnitSetupTab({ propertyId, className }: UnitSetupTabProps) {
  const isMobile = useIsMobile();
  const { data: items = [], isLoading } = useUnitItems(propertyId);
  const { data: propertyRooms = [] } = usePropertyRooms(propertyId);
  const createItem = useCreateUnitItem();
  const updateItem = useUpdateUnitItem();
  const deleteItem = useDeleteUnitItem();

  const { data: appliances = [] } = useAppliances(propertyId);
  const createAppliance = useCreateAppliance(propertyId);
  const updateAppliance = useUpdateAppliance(propertyId);
  const deleteAppliance = useDeleteAppliance(propertyId);
  const { data: guideEntries = [] } = usePropertyGuideEntries(propertyId);
  const createGuide = useCreatePropertyGuideEntry();
  const { data: ppm = [] } = usePropertyPreventativeMaintenance(propertyId);
  const createPpm = useCreateCustomPreventativeMaintenance();
  const disablePpm = useDisablePreventativeMaintenance();

  const enabledPpm = useMemo(() => ppm.filter((p) => p.enabled), [ppm]);
  const sortedItems = useMemo(() => sortUnitItems(items), [items]);
  const roomNames = useMemo(
    () =>
      [...propertyRooms]
        .sort((a, b) => a.created_at.localeCompare(b.created_at))
        .map((r) => r.name),
    [propertyRooms],
  );

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<PropertyUnitItem | null>(null);
  const [name, setName] = useState("");
  const [room, setRoom] = useState<string>("");
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [quantity, setQuantity] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [apDeleteId, setApDeleteId] = useState<string | null>(null);
  const [pmDeleteId, setPmDeleteId] = useState<string | null>(null);
  const [sidePanel, setSidePanel] = useState<SidePanel>(null);
  const { data: parts = [] } = usePropertyParts(propertyId);
  const [partOpen, setPartOpen] = useState(false);
  const [partEditing, setPartEditing] = useState<PropertyPart | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const subRaw = searchParams.get("sub");
  const sub: UnitSub = subRaw === "parts" || subRaw === "appliances" || subRaw === "guide" || subRaw === "checks" ? subRaw : "spots";
  const setSub = (v: UnitSub) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v === "spots") next.delete("sub");
        else next.set("sub", v);
        return next;
      },
      { replace: true },
    );
  const stampsSummary = useLocationStampsSummary(propertyId);
  const arrivalSummaryText = useArrivalChecklistSummary(propertyId);
  const [showAllItems, setShowAllItems] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Appliance mini form / edit
  const [apName, setApName] = useState("");
  const [apLocation, setApLocation] = useState("");
  const [apModel, setApModel] = useState("");
  const [apSerial, setApSerial] = useState("");
  const [apMake, setApMake] = useState("");
  const [apWarranty, setApWarranty] = useState("");
  const [apPhotos, setApPhotos] = useState<string[]>([]);
  const [apAdding, setApAdding] = useState(false);
  const [apEditing, setApEditing] = useState<Appliance | null>(null);
  const apFileRef = useRef<HTMLInputElement>(null);

  // Guide mini form
  const [gTitle, setGTitle] = useState("");
  const [gRoom, setGRoom] = useState("");
  const [gMedia, setGMedia] = useState<string | null>(null);
  const [gAdding, setGAdding] = useState(false);
  const gFileRef = useRef<HTMLInputElement>(null);

  // PM mini form
  const [pmTitle, setPmTitle] = useState("");
  const [pmFreq, setPmFreq] = useState<PreventativeFrequency>("monthly");
  const [pmPhoto, setPmPhoto] = useState<string | null>(null);
  const [pmAdding, setPmAdding] = useState(false);
  const [pmWho, setPmWho] = useState<"cleaner" | "team">("cleaner");
  const [pmDue, setPmDue] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [pmCleanerId, setPmCleanerId] = useState<string>("");
  const pmFileRef = useRef<HTMLInputElement>(null);
  const { members: assignableMembers = [] } = usePropertyAssignableMembers(propertyId);
  const cleanerOptions = useMemo(
    () => assignableMembers.filter((m) => !!m.cleanerId),
    [assignableMembers],
  );

  const deleteTarget = items.find((i) => i.id === deleteId) ?? null;
  const apDeleteTarget = appliances.find((a) => a.id === apDeleteId) ?? null;
  const pmDeleteTarget = enabledPpm.find((p) => p.id === pmDeleteId) ?? null;
  const pmDeleteTitle = pmDeleteTarget
    ? pmDeleteTarget.custom_title ??
      pmDeleteTarget.preventative_maintenance_templates?.title ??
      "Maintenance check"
    : "Maintenance check";

  const resolvedRoom = (): string | null => room.trim() || null;

  const resetItemSheet = () => {
    setEditing(null);
    setName("");
    setRoom(roomNames[0] || "");
    setPhotoUrl(null);
    setQuantity("");
  };

  const openAdd = () => {
    resetItemSheet();
    setRoom(roomNames[0] || "");
    setSheetOpen(true);
  };

  const openEdit = (item: PropertyUnitItem) => {
    setEditing(item);
    setName(item.name);
    const r = (item.room || "").trim();
    setRoom(r);
    setPhotoUrl(item.photo_url);
    setQuantity(item.quantity ? String(item.quantity) : "");
    setSheetOpen(true);
  };

  const closeSheet = () => {
    setSheetOpen(false);
    resetItemSheet();
  };

  const uploadToReference = async (file: File, prefix: string) => {
    const prepared = await prepareImageFileForUpload(file);
    const path = `${propertyId}/${prefix}/${crypto.randomUUID()}.jpg`;
    const { error } = await supabase.storage.from("reference-photos").upload(path, prepared, {
      contentType: "image/jpeg",
      upsert: false,
    });
    if (error) throw error;
    const { data } = supabase.storage.from("reference-photos").getPublicUrl(path);
    return data.publicUrl;
  };

  const uploadItemPhoto = async (file: File) => {
    setBusy(true);
    try {
      const url = await uploadToReference(file, "unit-items");
      setPhotoUrl(url);
      if (editing) {
        await updateItem.mutateAsync({
          id: editing.id,
          propertyId,
          photoUrl: url,
        });
      }
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleSaveItem = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast({ title: "Name required", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const roomValue = resolvedRoom();
      const qtyNum = parseInt(quantity.trim(), 10);
      const quantityValue = Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : null;
      if (editing) {
        await updateItem.mutateAsync({
          id: editing.id,
          propertyId,
          name: trimmed,
          room: roomValue,
          photoUrl,
          quantity: quantityValue,
        });
      } else {
        await createItem.mutateAsync({
          propertyId,
          name: trimmed,
          room: roomValue,
          photoUrl,
          sortOrder: items.length,
          quantity: quantityValue,
        });
      }
      toast({ title: "Saved" });
      closeSheet();
    } catch (err) {
      toast({
        title: "Couldn’t save",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setBusy(true);
    try {
      await deleteItem.mutateAsync({ id: deleteId, propertyId });
      toast({ title: "Deleted" });
      setDeleteId(null);
      if (editing?.id === deleteId) closeSheet();
    } catch (err) {
      toast({
        title: "Couldn’t delete",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteAppliance = async () => {
    if (!apDeleteId) return;
    setBusy(true);
    try {
      await deleteAppliance.mutateAsync(apDeleteId);
      toast({ title: "Appliance removed" });
      setApDeleteId(null);
      if (apEditing?.id === apDeleteId) {
        setApEditing(null);
        resetApForm();
      }
    } catch (err) {
      toast({
        title: "Couldn’t remove",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDeletePm = async () => {
    if (!pmDeleteId) return;
    setBusy(true);
    try {
      await disablePpm.mutateAsync({ id: pmDeleteId, propertyId });
      toast({ title: "Maintenance check removed" });
      setPmDeleteId(null);
    } catch (err) {
      toast({
        title: "Couldn’t remove",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const resetApForm = () => {
    setApName("");
    setApLocation("");
    setApModel("");
    setApSerial("");
    setApMake("");
    setApWarranty("");
    setApPhotos([]);
    setApAdding(false);
    setApEditing(null);
  };

  const openApEdit = (a: Appliance) => {
    setApAdding(false);
    setApEditing(a);
    setApName(a.name);
    setApLocation(a.location ?? "");
    setApModel(a.model_number ?? "");
    setApSerial(a.serial_number ?? "");
    setApMake(a.make ?? "");
    setApWarranty(a.warranty_expiry ?? "");
    setApPhotos(a.photo_urls ?? []);
    setSidePanel("appliances");
  };

  const saveAppliance = async () => {
    const trimmed = apName.trim();
    if (!trimmed) {
      toast({ title: "Name required", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      if (apEditing) {
        await updateAppliance.mutateAsync({
          id: apEditing.id,
          name: trimmed,
          location: apLocation.trim() || null,
          make: apMake.trim() || null,
          model_number: apModel.trim() || null,
          serial_number: apSerial.trim() || null,
          photo_urls: apPhotos,
          purchase_date: apEditing.purchase_date,
          warranty_expiry: apWarranty || null,
          notes: apEditing.notes,
          vendor_id: apEditing.vendor_id,
        });
      } else {
        await createAppliance.mutateAsync({
          name: trimmed,
          location: apLocation.trim() || null,
          make: apMake.trim() || null,
          model_number: apModel.trim() || null,
          serial_number: apSerial.trim() || null,
          warranty_expiry: apWarranty || null,
          photo_urls: apPhotos,
        });
      }
      toast({ title: "Saved" });
      resetApForm();
      setSidePanel(null);
    } catch (err) {
      toast({
        title: "Couldn’t save",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const saveGuide = async () => {
    const trimmed = gTitle.trim();
    if (!trimmed) {
      toast({ title: "Title required", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const isVideo = !!gMedia && /\.(mp4|webm|mov)(\?|$)/i.test(gMedia);
      await createGuide.mutateAsync({
        property_id: propertyId,
        title: trimmed,
        room: gRoom.trim() || null,
        category: "setup_standard",
        photo_urls: gMedia && !isVideo ? [gMedia] : [],
        video_url: gMedia && isVideo ? gMedia : null,
      });
      toast({ title: "Saved" });
      setGTitle("");
      setGRoom("");
      setGMedia(null);
      setGAdding(false);
      setSidePanel(null);
    } catch (err) {
      toast({
        title: "Couldn’t save",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const savePm = async () => {
    const trimmed = pmTitle.trim();
    if (!trimmed) {
      toast({ title: "Title required", variant: "destructive" });
      return;
    }
    if (!pmDue) {
      toast({ title: "Next due required", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await createPpm.mutateAsync({
        propertyId,
        title: trimmed,
        frequency: pmFreq,
        steps: [{ title: trimmed }],
        defaultAssignType: pmWho === "cleaner" ? "cleaner" : "team",
        defaultCleanerId: pmWho === "cleaner" ? pmCleanerId || null : null,
        defaultAssigneeUserId: null,
        notes: pmPhoto || null,
        next_scheduled_date: pmDue,
      });
      toast({ title: "Saved" });
      setPmTitle("");
      setPmFreq("monthly");
      setPmPhoto(null);
      setPmWho("cleaner");
      setPmDue(format(new Date(), "yyyy-MM-dd"));
      setPmCleanerId("");
      setPmAdding(false);
      setSidePanel(null);
    } catch (err) {
      toast({
        title: "Couldn’t save",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const itemForm = (
    <div className="flex flex-col gap-5 px-1 pb-2">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void uploadItemPhoto(f);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className={cn(
          "flex aspect-[4/3] w-full flex-col items-center justify-center gap-2 overflow-hidden rounded-2xl border-[1.5px] border-dashed",
          photoUrl ? "border-[#e6edf1] bg-[#f4f7f9]" : "border-[#c5d4dc] bg-[#f4f7f9]",
        )}
      >
        {photoUrl ? (
          <img src={photoUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <>
            <div className="text-[15px] font-bold text-[#24a89a]">Add photo</div>
            <div className="text-sm font-medium text-[#8494a2]">Baseline reference</div>
          </>
        )}
      </button>
      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-bold tracking-[0.4px] text-[#64748b]">NAME</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Floor lamp"
          className="h-12 rounded-xl border-[1.5px] border-[#e6edf1] bg-white px-4 text-[16px] font-medium text-[#0f1720] outline-none focus:border-[#24a89a]"
        />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-bold tracking-[0.4px] text-[#64748b]">
          QUANTITY <span className="font-medium">optional</span>
        </span>
        <input
          value={quantity}
          onChange={(e) => setQuantity(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
          inputMode="numeric"
          placeholder="e.g. 4"
          className="h-12 w-32 rounded-xl border-[1.5px] border-[#e6edf1] bg-white px-4 text-[16px] font-medium text-[#0f1720] outline-none focus:border-[#24a89a]"
        />
      </label>
      <div className="flex flex-col gap-2">
        <span className="text-xs font-bold tracking-[0.4px] text-[#64748b]">ROOM</span>
        {roomNames.length === 0 ? (
          <p className="text-[14px] font-medium text-[#8494a2]">
            Add rooms in the Rooms tab first — then pick one here.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {(room && !roomNames.includes(room) ? [...roomNames, room] : roomNames).map((r) => {
              const selected = room === r;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRoom(r)}
                  className={cn(
                    "h-11 rounded-xl px-4 text-[14px] font-bold",
                    selected ? "bg-[#24a89a] text-white" : "bg-[#f1f5f8] text-[#475569]",
                  )}
                >
                  {r}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => void handleSaveItem()}
        disabled={busy}
        className="flex h-14 items-center justify-center rounded-2xl bg-[#24a89a] text-[17px] font-bold text-white hover:bg-[#1f958a] disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save"}
      </button>
      {editing && (
        <button
          type="button"
          onClick={() => setDeleteId(editing.id)}
          className="text-[15px] font-bold text-[#b4453a]"
        >
          Delete
        </button>
      )}
    </div>
  );

  const sideContent = (panel: Exclude<SidePanel, null>) => {
    if (panel === "arrival") {
      return <ArrivalChecklistSettings propertyId={propertyId} onSaved={() => setSidePanel(null)} />;
    }
    if (panel === "stamps") {
      return <LocationStampsSettings propertyId={propertyId} onSaved={() => setSidePanel(null)} />;
    }
    if (panel === "appliances") {
      const apForm = (
        <div className="flex flex-col gap-3 rounded-2xl border-[1.5px] border-[#e6edf1] p-4">
          <input
            ref={apFileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f) return;
              setBusy(true);
              try {
                const url = await uploadToReference(f, "appliances");
                setApPhotos((prev) => [...prev, url].slice(0, 6));
              } catch (err) {
                toast({
                  title: "Upload failed",
                  description: err instanceof Error ? err.message : undefined,
                  variant: "destructive",
                });
              } finally {
                setBusy(false);
              }
            }}
          />
          <div className="flex flex-wrap items-center gap-3">
            {apPhotos.map((url) => (
              <img
                key={url}
                src={url}
                alt=""
                className="h-[78px] w-[78px] rounded-[13px] border border-[#e6edf1] object-cover"
              />
            ))}
            <button
              type="button"
              onClick={() => apFileRef.current?.click()}
              className="flex h-[78px] w-[78px] items-center justify-center rounded-[13px] border-[1.5px] border-dashed border-[#cdd8df] px-1 text-center text-[13px] font-bold leading-tight text-[#15776d]"
            >
              Add photo
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <div className="text-[11px] font-bold tracking-[0.8px] text-[#8494a2]">NAME</div>
              <input
                value={apName}
                onChange={(e) => setApName(e.target.value)}
                placeholder="Name"
                className="h-[46px] rounded-[11px] border border-[#cdd8df] px-3 text-[15px]"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="text-[11px] font-bold tracking-[0.8px] text-[#8494a2]">LOCATION</div>
              <input
                value={apLocation}
                onChange={(e) => setApLocation(e.target.value)}
                placeholder="Location"
                className="h-[46px] rounded-[11px] border border-[#cdd8df] px-3 text-[15px]"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="text-[11px] font-bold tracking-[0.8px] text-[#8494a2]">MODEL</div>
              <input
                value={apModel}
                onChange={(e) => setApModel(e.target.value)}
                placeholder="Model"
                className="h-[46px] rounded-[11px] border border-[#cdd8df] px-3 text-[15px]"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="text-[11px] font-bold tracking-[0.8px] text-[#8494a2]">SERIAL</div>
              <input
                value={apSerial}
                onChange={(e) => setApSerial(e.target.value)}
                placeholder="Serial"
                className="h-[46px] rounded-[11px] border border-[#cdd8df] px-3 text-[15px]"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="text-[11px] font-bold tracking-[0.8px] text-[#8494a2]">MAKE</div>
              <input
                value={apMake}
                onChange={(e) => setApMake(e.target.value)}
                placeholder="Make"
                className="h-[46px] rounded-[11px] border border-[#cdd8df] px-3 text-[15px]"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="text-[11px] font-bold tracking-[0.8px] text-[#8494a2]">WARRANTY ENDS</div>
              <input
                type="date"
                value={apWarranty}
                onChange={(e) => setApWarranty(e.target.value)}
                className="h-[46px] rounded-[11px] border border-[#cdd8df] px-3 text-[15px]"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 pt-1">
            {apEditing ? (
              <button
                type="button"
                onClick={() => setApDeleteId(apEditing.id)}
                className="px-1 py-2.5 text-[14.5px] font-bold text-[#b4453a]"
              >
                Delete
              </button>
            ) : null}
            <div className="flex-1" />
            <button
              type="button"
              onClick={resetApForm}
              className="flex h-[46px] items-center rounded-xl border-[1.5px] border-[#e6edf1] px-5 text-[15px] font-bold text-[#64748b]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void saveAppliance()}
              disabled={busy}
              className="flex h-[46px] items-center rounded-xl bg-[#24a89a] px-6 text-[15px] font-bold text-white disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
      );

      return <div className="flex min-w-0 flex-col gap-2.5 px-1 pb-4">{apForm}</div>;
    }
    if (panel === "guide") {
      return (
        <div className="flex flex-col gap-4 px-1 pb-4">
          <p className="text-sm font-medium text-[#8494a2]">
            Edit in CapCut; upload the final clip
          </p>
          {true ? (
            <div className="flex flex-col gap-3 rounded-2xl border-[1.5px] border-[#e6edf1] p-4">
              <input
                ref={gFileRef}
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  setBusy(true);
                  try {
                    const isVid = f.type.startsWith("video/");
                    const ext = isVid ? (f.name.split(".").pop() || "mp4") : "jpg";
                    const path = `${propertyId}/property-guide/${crypto.randomUUID()}.${ext}`;
                    const { error } = await supabase.storage
                      .from("reference-photos")
                      .upload(path, isVid ? f : await prepareImageFileForUpload(f), {
                        contentType: f.type || (isVid ? "video/mp4" : "image/jpeg"),
                        upsert: false,
                      });
                    if (error) throw error;
                    const { data } = supabase.storage.from("reference-photos").getPublicUrl(path);
                    setGMedia(data.publicUrl);
                  } catch (err) {
                    toast({
                      title: "Upload failed",
                      description: err instanceof Error ? err.message : undefined,
                      variant: "destructive",
                    });
                  } finally {
                    setBusy(false);
                  }
                }}
              />
              <button
                type="button"
                onClick={() => gFileRef.current?.click()}
                className="flex h-24 items-center justify-center rounded-xl border-dashed border-[1.5px] border-[#c5d4dc] bg-[#f4f7f9] text-[14px] font-bold text-[#24a89a]"
              >
                {gMedia ? "Media added" : "Add photo/video"}
              </button>
              <input
                value={gTitle}
                onChange={(e) => setGTitle(e.target.value)}
                placeholder="Title"
                className="h-11 rounded-xl border-[1.5px] border-[#e6edf1] px-3 text-[15px]"
              />
              <input
                value={gRoom}
                onChange={(e) => setGRoom(e.target.value)}
                placeholder="Room"
                className="h-11 rounded-xl border-[1.5px] border-[#e6edf1] px-3 text-[15px]"
              />
              <button
                type="button"
                onClick={() => void saveGuide()}
                disabled={busy}
                className="h-12 rounded-xl bg-[#24a89a] text-[15px] font-bold text-white"
              >
                Save
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setGAdding(true)}
              className="h-12 rounded-xl bg-[#24a89a] text-[15px] font-bold text-white"
            >
              + Clip
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-4 px-1 pb-4">
        <p className="text-sm font-medium text-[#8494a2]">Not part of every cleaner Walk</p>
        {true ? (
          <div className="flex flex-col gap-3 rounded-2xl border-[1.5px] border-[#e6edf1] p-4">
            <input
              ref={pmFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (!f) return;
                setBusy(true);
                try {
                  setPmPhoto(await uploadToReference(f, "unit-pm"));
                } catch (err) {
                  toast({
                    title: "Upload failed",
                    description: err instanceof Error ? err.message : undefined,
                    variant: "destructive",
                  });
                } finally {
                  setBusy(false);
                }
              }}
            />
            <button
              type="button"
              onClick={() => pmFileRef.current?.click()}
              className="flex h-24 flex-col items-center justify-center gap-1 overflow-hidden rounded-xl border-dashed border-[1.5px] border-[#c5d4dc] bg-[#f4f7f9] text-[14px] font-bold text-[#24a89a]"
            >
              {pmPhoto ? (
                <img src={pmPhoto} alt="" className="h-full w-full object-cover" />
              ) : (
                <>
                  Add photo
                  <span className="px-3 text-center text-[11px] font-medium text-[#8494a2]">
                    Optional. A host photo of the filter slot or alarm saves a question later.
                  </span>
                </>
              )}
            </button>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-bold tracking-[0.4px] text-[#64748b]">TITLE</span>
              <input
                value={pmTitle}
                onChange={(e) => setPmTitle(e.target.value)}
                placeholder="Title"
                className="h-11 rounded-xl border-[1.5px] border-[#e6edf1] px-3 text-[15px]"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-bold tracking-[0.4px] text-[#64748b]">NEXT DUE</span>
              <input
                type="date"
                value={pmDue}
                onChange={(e) => setPmDue(e.target.value)}
                className="h-11 rounded-xl border-[1.5px] border-[#e6edf1] px-3 text-[15px] font-medium"
              />
            </label>
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold tracking-[0.4px] text-[#64748b]">FREQUENCY</span>
              <div className="flex flex-wrap gap-2">
                {PM_FREQUENCIES.map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setPmFreq(f)}
                    className={cn(
                      "h-10 rounded-xl px-3 text-[13px] font-bold",
                      pmFreq === f ? "bg-[#24a89a] text-white" : "bg-[#f1f5f8] text-[#475569]",
                    )}
                  >
                    {getFrequencyLabel(f).split(" ")[0]}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold tracking-[0.4px] text-[#64748b]">WHO</span>
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    { id: "cleaner" as const, label: "Cleaner", sub: "Shows on their job when due" },
                    { id: "team" as const, label: "Team", sub: "In-app notify when due" },
                  ] as const
                ).map((opt) => {
                  const active = pmWho === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setPmWho(opt.id)}
                      className={cn(
                        "flex flex-col items-start gap-0.5 rounded-[14px] border-[1.5px] px-3 py-2.5 text-left",
                        active
                          ? "border-[#24a89a] bg-[#e2eeed]"
                          : "border-[#e6edf1] bg-white",
                      )}
                    >
                      <span className={cn("text-[15px] font-bold", active ? "text-[#15776d]" : "text-[#0f1720]")}>
                        {opt.label}
                      </span>
                      <span className="text-[11px] font-medium text-[#8494a2]">{opt.sub}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {pmWho === "cleaner" ? (
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-bold tracking-[0.4px] text-[#64748b]">ASSIGN</span>
                <select
                  value={pmCleanerId}
                  onChange={(e) => setPmCleanerId(e.target.value)}
                  className="h-11 rounded-xl border-[1.5px] border-[#e6edf1] bg-white px-3 text-[15px] font-medium"
                >
                  <option value="">Unassigned</option>
                  {cleanerOptions.map((m) => (
                    <option key={m.value} value={m.cleanerId || ""}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <p className="text-[13px] font-medium text-[#8494a2]">
                We’ll notify the team in the app when it’s due.
              </p>
            )}
            <button
              type="button"
              onClick={() => void savePm()}
              disabled={busy}
              className="h-12 rounded-xl bg-[#24a89a] text-[15px] font-bold text-white"
            >
              Save
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setPmPhoto(null);
              setPmAdding(true);
            }}
            className="h-12 rounded-xl border-[1.5px] border-[#cfe4e1] bg-white text-[15px] font-bold text-[#15776d]"
          >
            + Maintenance check
          </button>
        )}
      </div>
    );
  };

  const sideTitle =
    sidePanel === "appliances"
      ? apEditing
        ? "Edit appliance"
        : "Appliances"
      : sidePanel === "guide"
        ? "Guide clips"
        : sidePanel === "stamps"
          ? "Location stamps"
          : sidePanel === "arrival"
            ? "Arrival checklist"
            : "Maintenance checks";

  if (isLoading) {
    return (
      <div className={cn("flex justify-center py-16 text-[#64748b]", className)}>
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const previewItems = showAllItems ? sortedItems : sortedItems.slice(0, 4);
  const moreCount = Math.max(0, sortedItems.length - 4);

  return (
    <div className={cn("flex flex-col gap-5", className)} data-tour="tour-unit-setup">
      <h2 className="sr-only">Unit</h2>
      <UnitSubTabs
        value={sub}
        onChange={setSub}
        counts={{
          spots: null,
          parts: parts.length,
          appliances: appliances.length,
          guide: guideEntries.length,
          checks: enabledPpm.length,
        }}
      />

      {sub === "spots" ? <PhotoGroupsSection propertyId={propertyId} onAddItem={openAdd} /> : null}

      {sub === "parts" ? (
        <UnitPartsPage
          propertyId={propertyId}
          parts={parts}
          onAdd={() => {
            setPartEditing(null);
            setPartOpen(true);
          }}
          onEdit={(p) => {
            setPartEditing(p);
            setPartOpen(true);
          }}
        />
      ) : null}

      {sub === "appliances" ? (
        <UnitAppliancesPage
          appliances={appliances}
          roomNames={roomNames}
          onAdd={() => {
            resetApForm();
            setApAdding(true);
            setSidePanel("appliances");
          }}
          onEdit={openApEdit}
        />
      ) : null}

      {sub === "guide" ? (
        <UnitGuideClipsPage
          entries={guideEntries}
          onAdd={() => {
            setGAdding(true);
            setSidePanel("guide");
          }}
        />
      ) : null}

      {sub === "checks" ? (
        <div className="flex flex-col gap-4">
          <section className="flex flex-col gap-3 rounded-[24px] bg-white p-5">
            <div>
              <div className="text-[19px] font-bold tracking-[-0.3px] text-[#0f1720]">Arrival checklist</div>
              <div className="text-[13.5px] font-medium text-[#64748b]">What the cleaner does before cleaning starts.</div>
            </div>
            <ArrivalChecklistSettings propertyId={propertyId} onSaved={() => undefined} />
          </section>

          <UnitQuickCheckSection propertyId={propertyId} />

          <section className="flex flex-col gap-3 rounded-[24px] bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-[19px] font-bold tracking-[-0.3px] text-[#0f1720]">Maintenance checks</div>
                <div className="text-[13.5px] font-medium text-[#64748b]">Recurring · who does it · when it&apos;s due</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPmAdding(true);
                  setSidePanel("pm");
                }}
                className="flex h-11 shrink-0 items-center rounded-[14px] bg-[#15776d] px-4 text-[14.5px] font-bold text-white"
              >
                Add check
              </button>
            </div>
            {enabledPpm.length === 0 ? (
              <div className="rounded-[16px] bg-[#f4f7f9] px-4 py-8 text-center text-[14.5px] font-medium text-[#64748b]">
                Nothing recurring yet. Add filters to change, alarms to test, anything that needs checking regularly.
              </div>
            ) : (
              <div className="overflow-hidden rounded-[18px] border border-[#eef2f5]">
                {enabledPpm.map((pm, i) => {
                  const title = pm.custom_title ?? pm.preventative_maintenance_templates?.title ?? "Check";
                  const who =
                    pm.default_assign_type === "cleaner" ? "Cleaner" : pm.default_assign_type === "team" || pm.default_assign_type === "owner" ? "Team" : "Unassigned";
                  const due = pm.next_scheduled_date ? new Date(pm.next_scheduled_date + "T12:00:00") : null;
                  const overdue = !!due && due.getTime() < Date.now() - 86_400_000;
                  const photo = pm.notes && /^https?:\/\//i.test(pm.notes.trim()) ? pm.notes.trim() : null;
                  return (
                    <div key={pm.id} className={cn("flex items-center gap-3 px-4 py-3", i > 0 && "border-t border-[#eef2f5]")}>
                      <div className="h-12 w-12 shrink-0 overflow-hidden rounded-[12px] bg-[#eff4f7]">
                        {photo ? <img src={photo} alt="" className="h-full w-full object-cover" /> : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[15.5px] font-bold text-[#0f1720]">{title}</div>
                        <div className="text-[12.5px] font-medium text-[#8494a2]">
                          {getFrequencyLabel(pm.frequency)} · {who}
                        </div>
                      </div>
                      {due ? (
                        <span
                          className={cn(
                            "shrink-0 rounded-full px-3 py-1 text-[12.5px] font-bold",
                            overdue ? "bg-[#fdf1ef] text-[#b4453a]" : "bg-[#eef2f5] text-[#475569]",
                          )}
                        >
                          {overdue ? "Overdue · " : ""}
                          {format(due, "MMM d")}
                        </span>
                      ) : null}
                      <button type="button" onClick={() => setPmDeleteId(pm.id)} className="shrink-0 px-1 py-2 text-[13.5px] font-bold text-[#b4453a]">
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="flex flex-col gap-3 rounded-[24px] bg-white p-5">
            <div>
              <div className="text-[19px] font-bold tracking-[-0.3px] text-[#0f1720]">Location stamps</div>
              <div className="text-[13.5px] font-medium text-[#64748b]">Where each clean starts and ends, next to the clock times.</div>
            </div>
            <LocationStampsSettings propertyId={propertyId} onSaved={() => undefined} />
          </section>
        </div>
      ) : null}

      <PartDialog
        open={partOpen}
        onClose={() => setPartOpen(false)}
        propertyId={propertyId}
        part={partEditing}
        roomNames={roomNames}
        appliances={appliances}
      />

      {isMobile ? (
        <Drawer open={sheetOpen} onOpenChange={(o) => !o && closeSheet()}>
          <DrawerContent className="max-h-[92vh] px-4 pb-8">
            <DrawerHeader>
              <DrawerTitle>{editing ? "Edit item" : "Add item"}</DrawerTitle>
            </DrawerHeader>
            <div className="overflow-y-auto">{itemForm}</div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={sheetOpen} onOpenChange={(o) => !o && closeSheet()}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit item" : "Add item"}</DialogTitle>
            </DialogHeader>
            {itemForm}
          </DialogContent>
        </Dialog>
      )}

      {isMobile ? (
        <Drawer
          open={!!sidePanel}
          onOpenChange={(o) => {
            if (!o) setSidePanel(null);
          }}
        >
          <DrawerContent className="max-h-[92vh] px-4 pb-8">
            <DrawerHeader>
              <DrawerTitle>{sideTitle}</DrawerTitle>
            </DrawerHeader>
            <div className="overflow-y-auto">{sidePanel ? sideContent(sidePanel) : null}</div>
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog
          open={!!sidePanel}
          onOpenChange={(o) => {
            if (!o) setSidePanel(null);
          }}
        >
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{sideTitle}</DialogTitle>
            </DialogHeader>
            {sidePanel ? sideContent(sidePanel) : null}
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete item?</DialogTitle>
          </DialogHeader>
          <p className="text-[15px] font-medium text-[#64748b]">
            {deleteTarget?.name || "This item"} will leave the Unit baseline.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              onClick={() => setDeleteId(null)}
              className="h-11 rounded-xl px-4 text-[15px] font-bold text-[#64748b]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={busy}
              className="h-11 rounded-xl bg-[#b4453a] px-5 text-[15px] font-bold text-white"
            >
              Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!apDeleteId} onOpenChange={(o) => !o && setApDeleteId(null)}>
        <DialogContent className="max-w-sm gap-0 overflow-hidden rounded-[22px] p-0 sm:rounded-[22px]">
          <div className="flex flex-col gap-[18px] px-5 pb-7 pt-6">
            <div className="flex flex-col gap-1.5">
              <DialogTitle className="text-[20px] font-bold tracking-[-0.3px]">
                Delete {apDeleteTarget?.name || "appliance"}?
              </DialogTitle>
              <p className="text-[14px] font-medium leading-snug text-[#64748b]">
                Removes the appliance, its photos, model and serial from this unit.
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <button
                type="button"
                onClick={() => void handleDeleteAppliance()}
                disabled={busy}
                className="flex h-[52px] items-center justify-center rounded-[14px] bg-[#b4453a] text-[16px] font-bold text-white disabled:opacity-50"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setApDeleteId(null)}
                className="flex h-[52px] items-center justify-center rounded-[14px] border-[1.5px] border-[#e6edf1] text-[16px] font-bold text-[#64748b]"
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pmDeleteId} onOpenChange={(o) => !o && setPmDeleteId(null)}>
        <DialogContent className="max-w-sm gap-0 overflow-hidden rounded-[22px] p-0 sm:rounded-[22px]">
          <div className="flex flex-col gap-[18px] px-5 pb-7 pt-6">
            <div className="flex flex-col gap-1.5">
              <DialogTitle className="text-[20px] font-bold tracking-[-0.3px]">
                Remove {pmDeleteTitle}?
              </DialogTitle>
              <p className="text-[14px] font-medium leading-snug text-[#64748b]">
                Stops this check from showing on cleaner jobs or team notifies. You can add it again later.
              </p>
            </div>
            <div className="flex flex-col gap-2.5">
              <button
                type="button"
                onClick={() => void handleDeletePm()}
                disabled={busy}
                className="flex h-[52px] items-center justify-center rounded-[14px] bg-[#b4453a] text-[16px] font-bold text-white disabled:opacity-50"
              >
                Remove
              </button>
              <button
                type="button"
                onClick={() => setPmDeleteId(null)}
                className="flex h-[52px] items-center justify-center rounded-[14px] border-[1.5px] border-[#e6edf1] text-[16px] font-bold text-[#64748b]"
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
